import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { DateField } from '@/components/form/fields/date/DateInput';
import { ConfirmField } from '@/components/form/fields/text/ConfirmInput';
import { Form } from '@/components/form/Form';
import { Loading } from '@/components/loading/Loading';
import { Modal } from '@/components/modal/Modal';
import { ModalProps } from '@/components/modal/useModal';
import { Card } from '@/components/sections/Card';
import {
  UpdateFilterPackArgs,
  useCreateProductFilter,
  useFilterPack,
  useUpdateFilterPack
} from '@/services/api/hooks/catalog';
import { useProduct } from '@/services/api/hooks/product';
import { useCatalogueService } from '@/services/catalogue/useCatalogueService';
import { zodResolver } from '@hookform/resolvers/zod';
import { FunctionComponent, Suspense } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';

const HideProductFormFields = z.object({
  effectiveDate: z.date(),
  isConfirmed: z.literal(true)
});

type HideProductFormFields = z.infer<typeof HideProductFormFields>;

const HideProductModal: FunctionComponent<ModalProps<{ productId: string }>> = props => {
  const HideProduct = () => {
    if (!props.props?.productId) throw new Error('No product ID provided');
    const [product] = useProduct(props.props.productId);
    const methods = useForm<HideProductFormFields>({
      resolver: zodResolver(HideProductFormFields),
      defaultValues: { effectiveDate: new Date() }
    });
    const navigate = useNavigate();

    const createFilter = useCreateProductFilter();
    const filterPackId = useCatalogueService(state => state.filterPackId);
    if (!filterPackId) throw new Error('Missing filter pack id');
    const [filterPack] = useFilterPack(filterPackId);
    const updateFilterPack = useUpdateFilterPack();

    const onSubmit = async (data: HideProductFormFields) => {
      const filter = await createFilter.mutateAsync({
        productId: product.id,
        effectiveDate: data.effectiveDate
      });

      const updatedFilterPack: UpdateFilterPackArgs = {
        id: filterPack.id,
        globalMarginModifier: filterPack.globalMarginModifier,
        marginFilterIds: filterPack.marginFilters.map(filter => filter.id),
        productFilterIds: [...filterPack.productFilters.map(filter => filter.id), filter.id]
      };
      await updateFilterPack.mutateAsync(updatedFilterPack);
      toast.success('Successfully hidden product');
      navigate(`/configuratorCatalogue/${product.category.id}`);
    };

    return (
      <Card className="w-full p-8">
        <Modal.Controls {...props}>
          <h1 className="text-2xl font-semibold">Hide product</h1>
        </Modal.Controls>
        <div className="mt-1 space-y-2">
          <p>
            Hiding a product without replacing it will cause it to disappear from all catalogues and bundles with little
            warning. This should be reserved for situations where you’re confident it won’t impact customers.
          </p>
        </div>
        <Form className="flex w-full flex-col gap-3 py-3" methods={methods} onSubmit={onSubmit}>
          <DateField label="Effective from" id="effectiveDate" />
          <ConfirmField
            label={`Confirm by typing the name of ${product.name}`}
            id="isConfirmed"
            target={product.name}
          />

          <div className="flex justify-end space-x-2">
            <Button type="button" className="w-fit" variant={ButtonVariant.Mono} onClick={() => props.close()}>
              Cancel
            </Button>
            <Button
              className="w-fit"
              variant={ButtonVariant.Destructive}
              loading={createFilter.isLoading || updateFilterPack.isLoading}
              type="submit"
            >
              Confirm
            </Button>
          </div>
        </Form>
      </Card>
    );
  };

  return (
    <Modal className="md:max-w-xl" {...props}>
      <Suspense fallback={<Loading />}>
        <HideProduct />
      </Suspense>
    </Modal>
  );
};

export default HideProductModal;
